import React from 'react'
import Menu from 'components/menu'
import { IContextMenuState } from 'types'

/** A popover's width unless it asks for another: room for a 140px column of controls beside labels. */
export const POPOVER_WIDTH = 256

// Between the trigger and the popover.
const GAP = 4

// A popover opened from another overlay sits above it. The table's toolbar panels (Filter, Colour, …)
// and their lists stand at up to 3100, so a nested popover goes just above them; one opened from it
// in turn (a colour field's picker) comes later in the page, so it lands on top at the same level.
const NESTED_Z_INDEX = 3200

/**
 * Where a popover opens for its trigger: below it, or above it when there isn't room below for the
 * popover's expected height, so it never covers its trigger. In a window too short for either, it
 * takes whichever side has more room (and scrolls), rather than squeezing into a sliver above. It
 * lines up with the trigger's end edge (for a trigger at the end of a row) or its start (for one at
 * the start of a column).
 */
export const popoverPlacement = (
  trigger: DOMRect,
  { align = 'end', width = POPOVER_WIDTH, height }: { align?: 'start' | 'end'; width?: number; height: number }
): IContextMenuState => {
  const roomBelow = window.innerHeight - trigger.bottom - GAP
  const roomAbove = trigger.top - GAP
  const fitsBelow = roomBelow >= height || (roomAbove < height && roomBelow >= roomAbove)
  const left = align === 'start' ? trigger.left : Math.max(8, trigger.right - width)
  return {
    open: true,
    top: fitsBelow ? `${trigger.bottom + GAP}px` : 'auto',
    bottom: fitsBelow ? 'auto' : `${window.innerHeight - trigger.top + GAP}px`,
    left: `${left}px`,
    right: 'auto'
  }
}

interface PopoverProps {
  id: string
  /** Where it's open, from popoverPlacement; closed while null. */
  at: IContextMenuState | null
  /** Clicking away or pressing Escape. */
  onClose: () => void
  /** Its accessible name. */
  label: string
  /** Its heading. A popover whose content explains itself can leave it out. */
  title?: string
  /** A line under the title saying what's in it. */
  description?: string
  /** Buttons at its foot, at the right: Cancel and Save, for a popover that saves on Save. */
  footer?: React.ReactNode
  width?: number
  /** Opened from inside another popover, which a click in this one shouldn't close. */
  nested?: boolean
  children: React.ReactNode
}

/**
 * A popover, in the manner of shadcn/ui's: the menus' frame (border, corners, shadow, and closing on a
 * click away or Escape) holding a title and a line saying what's in it, its content 12px in from the
 * edge, and any buttons at its foot, below a hairline, as the menus' footers are. Open it from a
 * trigger with popoverPlacement.
 */
const Popover: React.FC<PopoverProps> = ({
  id,
  at,
  onClose,
  label,
  title,
  description,
  footer,
  width = POPOVER_WIDTH,
  nested = false,
  children
}) =>
  at && (
    <Menu
      id={id}
      menuState={at}
      setMenuState={(state) => {
        if (!state.open) onClose()
      }}
      width={width}
      fixed
      nested={nested}
      zIndex={nested ? NESTED_Z_INDEX : undefined}
    >
      <div role="dialog" aria-label={label} className="whitespace-normal text-[13px]">
        <div className="flex flex-col gap-3 p-3">
          {(title || description) && (
            <div className="flex flex-col gap-1">
              {title && <div className="font-medium leading-none text-body">{title}</div>}
              {description && <p className="text-muted">{description}</p>}
            </div>
          )}
          {children}
        </div>
        {footer && <div className="flex justify-end gap-2 border-t border-divider px-3 py-2.5">{footer}</div>}
      </div>
    </Menu>
  )

export default Popover
